/**
 * Noise gate for the outbound mic: passes audio only while its level is above
 * a threshold. The agent's own voice (close to the mic) is loud; coworkers
 * nearby are quieter — so a tuned threshold lets the agent through and cuts
 * the neighbors. This is the fix for "the customer hears the person next to
 * me almost as loud as me": denoisers (RNNoise / browser NS) deliberately
 * PRESERVE human speech — including the neighbor's — a gate is what actually
 * blocks it.
 *
 * Implementation notes:
 * - Level is sampled ~20x/sec with setInterval, not requestAnimationFrame —
 *   rAF freezes in background tabs, which would freeze the gate mid-call.
 *   (Tabs with live WebRTC/getUserMedia are exempt from aggressive timer
 *   throttling, so 50ms ticks keep running during a call.)
 * - The gate never fully mutes: closed = FLOOR_GAIN, so even a mis-tuned
 *   threshold can only make the agent quiet — never silent.
 * - A short hold keeps the gate open between words so speech doesn't chatter.
 */
export interface GatedStream {
  stream: MediaStream;
  dispose: () => void;
}

const FLOOR_GAIN = 0.08;   // closed-gate residual (≈ -22dB) — audible safety net
const HOLD_MS = 450;       // stay open this long after the level drops (word gaps)
const OPEN_SMOOTH = 0.01;  // fast attack — don't clip the first syllable
const CLOSE_SMOOTH = 0.09; // gentle release — no hard chop

export function gateStream(input: MediaStream, thresholdPct: number): GatedStream {
  const ctx = new AudioContext();
  const src = ctx.createMediaStreamSource(input);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  const gain = ctx.createGain();
  const dest = ctx.createMediaStreamDestination();
  src.connect(analyser);
  src.connect(gain);
  gain.connect(dest);

  // Map the 0-100 UI threshold onto 0..0.5 of full scale: normal speech close
  // to a mic peaks well above 0.5, so even threshold=100 can't block the agent.
  const threshold = Math.max(0, Math.min(100, thresholdPct)) / 100 * 0.5;

  const buf = new Uint8Array(analyser.frequencyBinCount);
  let open = true; // start open — never clip the greeting
  let holdUntil = 0;
  const timer = setInterval(() => {
    analyser.getByteTimeDomainData(buf);
    let peak = 0;
    for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
    const level = peak / 128;
    const now = Date.now();
    if (level >= threshold) {
      open = true;
      holdUntil = now + HOLD_MS;
    } else if (now > holdUntil) {
      open = false;
    }
    gain.gain.setTargetAtTime(open ? 1 : FLOOR_GAIN, ctx.currentTime, open ? OPEN_SMOOTH : CLOSE_SMOOTH);
  }, 50);

  return {
    stream: dest.stream,
    dispose: () => {
      clearInterval(timer);
      try {
        void ctx.close();
      } catch {
        /* already closed */
      }
    },
  };
}
